import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import OpticksBreadcrumbs from "@/components/ui/OpticksBreadcrumbs/OpticksBreadcrumbs";
import OpticksTitleViewSection from "@/components/ui/OpticksTitleViewSection/OpticksTitleViewSection";
import OpticksIntegrationStep from "@/pages/setup-center/integrations/components/steps/OpticksIntegrationStep";
import { usePixelsStore, selectPixelData } from "@/zustand/pixelsStore";
import { getChannelsLinks } from "@/services/api/ChannelsRepository";
import type {
  ChannelIntegrationConfig,
  IntegrationFlow,
} from "./ChannelIntegrationGuide.types";
import ChooseIntegrationMethod from "./components/ChooseIntegrationMethod";
import LinkAccountStep from "./components/LinkAccountStep";
import IntegrationGuideStep from "./components/IntegrationGuideStep";
import FinishUpStep from "./components/FinishUpStep";
import "./ChannelIntegrationGuide.css";

interface Props {
  config: ChannelIntegrationConfig;
  isOAuthAvailable?: boolean;
}

const ChannelIntegrationGuide = ({
  config,
  isOAuthAvailable = false,
}: Props) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const pixelData = usePixelsStore(selectPixelData(config.platformKey));
  const { setPixelData } = usePixelsStore();

  const [flow, setFlow] = useState<IntegrationFlow>(() => {
    const flowParam = searchParams.get("flow");
    return flowParam === "oauth" ? "oauth" : null;
  });

  const isLinked = pixelData.length > 0;
  const oAuthAvailable = config.supportsOAuth && (isOAuthAvailable ?? false);

  useEffect(() => {
    getChannelsLinks()
      .then((res) => {
        const links = (res?.links || []).filter(
          (link: { platform: string }) => link.platform === config.platformKey
        );
        setPixelData(config.platformKey, links);
      })
      .catch(() => {});
  }, []);

  // Clean up ?flow=oauth from URL after reading it
  useEffect(() => {
    if (searchParams.has("flow")) {
      searchParams.delete("flow");
      setSearchParams(searchParams, { replace: true });
    }
  }, []);

  // For non-OAuth channels, default to manual flow
  useEffect(() => {
    if (!oAuthAvailable && flow === null) {
      setFlow("manual");
    }
  }, [oAuthAvailable, flow]);

  const steps = useMemo(() => {
    const result: Array<{
      title: string;
      content: React.ReactNode;
    }> = [];

    if (oAuthAvailable) {
      result.push({
        title: "Choose how to integrate",
        content: (
          <ChooseIntegrationMethod
            selectedFlow={flow}
            onSelectFlow={setFlow}
            channelName={config.channelDisplayName}
          />
        ),
      });

      if (flow === "oauth") {
        result.push({
          title: "Link your account",
          content: (
            <LinkAccountStep
              channelDisplayName={config.channelDisplayName}
              oauthPlatformKey={config.oauthPlatformKey}
              linkingPath={config.linkingPath}
              isLinked={isLinked}
            />
          ),
        });
      }
    }

    const guideStepNumber = result.length + 1;
    result.push({
      title: "Integration guide",
      content: (
        <IntegrationGuideStep
          guideSteps={config.guideSteps}
          flow={flow}
          parentStepNumber={guideStepNumber}
        />
      ),
    });

    result.push({
      title: "Finish up",
      content: (
        <FinishUpStep
          flow={flow}
          channelDisplayName={config.channelDisplayName}
          pixelManagementPath={config.pixelManagementPath}
        />
      ),
    });

    return result;
  }, [oAuthAvailable, flow, isLinked, config]);

  return (
    <div className="layout-center">
      <div className="breadcrumbs-title">
        <OpticksBreadcrumbs
          config={[
            {
              name: config.channelName,
              path: config.overviewPath,
            },
            { name: "Integration", path: "" },
          ]}
        />

        <OpticksTitleViewSection
          type="view"
          icon={config.icon}
          title="Integration"
          subtitle={
            <p>
              Get started to prevent fraud in your {config.channelName}{" "}
              campaigns. Follow the steps below to integrate{" "}
              {config.channelDisplayName} with Opticks:
            </p>
          }
        />
      </div>

      <div className="channel-integration-guide">
        {steps.map((step, index) => (
          <OpticksIntegrationStep
            key={`${step.title}-${index}`}
            stepNumber={index + 1}
            stepTitle={step.title}
            content={step.content}
          />
        ))}
      </div>
    </div>
  );
};

export default ChannelIntegrationGuide;
